<template>
  <InfuseModal :title="t('e_connector.centralSystems.modal.create.title')" @close="emit('close')">
    <form ref="form" id="create-central-system" @submit.prevent="onSave()">
      <div class="flex max-h-[475px] max-w-[965px] flex-col gap-4 overflow-y-auto px-5">
        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.name')" class="basis-full" size="small">
            <InfuseInput
              size="small"
              v-model="name"
              :maxlength="255"
              :placeholder="t('e_connector.centralSystems.modal.field.name.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.url')" class="basis-full" size="small">
            <InfuseInput
              type="url"
              size="small"
              v-model="uri"
              :maxlength="255"
              :placeholder="t('e_connector.centralSystems.modal.field.uri.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div class="flex items-start gap-4">
          <InfuseField :label="t('e_connector.common.centralSystem.type')" class="basis-1/2" size="small">
            <InfuseSelect
              name="type"
              size="small"
              v-model="type"
              :options="typeOptions"
              :label="t('e_connector.centralSystems.modal.field.type.placeholder')"
              :required="true"
            />
          </InfuseField>

          <InfuseField :label="t('e_connector.common.centralSystem.ocppVersion')" class="basis-1/2" size="small">
            <InfuseSelect
              name="ocppVersion"
              size="small"
              v-model="ocppVersion"
              :options="ocppVersionOptions"
              :label="t('e_connector.centralSystems.modal.field.ocppVersion.placeholder')"
              :required="true"
            />
          </InfuseField>
        </div>

        <div v-if="isDomainUser" class="flex items-start gap-4">
          <InfuseField :label="t('common.visibility.label')" class="basis-1/2" size="small">
            <InfuseSelect
              name="visibility"
              size="small"
              v-model="visibility"
              :options="visibilityOptions"
              :format="formatVisibilityLabel"
              :label="t('common.visibility.option.private.label')"
            />
          </InfuseField>
        </div>
      </div>
    </form>

    <template #actions>
      <InfuseButton color="secondary" @click="emit('close')">
        {{ t('e_connector.common.action.cancel') }}
      </InfuseButton>
      <InfuseButton type="submit" form="create-central-system" :disabled="loading" :loading="loading">
        {{ t('e_connector.common.action.create') }}
      </InfuseButton>
    </template>
  </InfuseModal>
</template>

<script setup lang="ts">
import { InfuseButton, InfuseField, InfuseInput, InfuseModal, InfuseSelect } from '@infuse/ui/components';
import { inject, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CreateCentralSystem } from '@infuse/api/e-connector/central-system';
import { AbacVisibility } from '@infuse/api/common';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { UserLevel } from '@infuse/iam/user/type';
import { ConnectionType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { ConnectorConfig } from '@infuse/e-connector/config/plugin';

interface Props {
  loading: boolean;
}

interface Emits {
  (e: 'close'): void;
  (e: 'create', createCentralSystem: CreateCentralSystem): void;
}

withDefaults(defineProps<Props>(), {
  loading: false
});

const emit = defineEmits<Emits>();

const { t } = useI18n();

const currentUserStore = useCurrentUserStore();
const isDomainUser = currentUserStore.enabled && currentUserStore.level === UserLevel.DOMAIN;

const name = ref<string>('');
const uri = ref<string>('');
const type = ref<ConnectionType>(ConnectionType.WEBSOCKET);
const ocppVersion = ref<OcppVersion>(OcppVersion.V16);
const visibility = ref<AbacVisibility | undefined>(undefined);

const config = inject<Ref<ConnectorConfig>>('connectorConfig')?.value;
const typeOptions = Object.values(ConnectionType).filter((v) => {
  switch (v) {
    case ConnectionType.AWS_IOT:
      return !!config?.isAwsIotAvailable;
    case ConnectionType.AWS_SNS:
      return !!config?.isAwsSnsAvailable;
    case ConnectionType.JAVA_API:
      return false;
    default:
      return true;
  }
});
const ocppVersionOptions = Object.values(OcppVersion);
const visibilityOptions: { key: string; value: AbacVisibility }[] = [
  { key: 'PRIVATE', value: AbacVisibility.PRIVATE },
  { key: 'GLOBAL', value: AbacVisibility.GLOBAL }
];

function formatVisibilityLabel(key: string): string {
  return t(`common.visibility.option.${key.toLowerCase()}.label`);
}

function onSave() {
  emit('create', {
    name: name.value,
    uri: uri.value,
    type: type.value as ConnectionType,
    ocppVersion: ocppVersion.value as OcppVersion,
    visibility: visibility.value
  });
}
</script>
